import React, { useEffect, useState, useLayoutEffect } from 'react';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle } from '@phosphor-icons/react';
interface CaseStudyAIComparisonProps {
  isOverlay?: boolean;
}
export function CaseStudyAIComparison({
  isOverlay = false
}: CaseStudyAIComparisonProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  // ADDED: Check if user came from chat OR home page
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  // ADDED: Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Reset scroll position IMMEDIATELY before navigation
    window.scrollTo(0, 0);
    const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
    navigate(homePath);
  };
  // Handle chat state when navigating to this case study
  useCaseStudyNavigation();
  // Determine home path based on view preference
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  // Ensure page starts at top - no animation
  useLayoutEffect(() => {
    // Set scroll position instantly without animation
    window.scrollTo(0, 0);
    // Set manual scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  // Handle smooth scrolling for anchor links
  const handleSmoothScroll = (targetId: string) => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop,
        behavior: 'smooth'
      });
    }
  };
  // Dummy handler for ChatDrawer
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  return <>
      {!isOverlay && <CustomCursor data-id="element-466" />}

      <div className="flex min-h-screen" data-id="element-467">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-468">
          <div className="w-full min-h-screen bg-white text-black font-switzer" data-id="element-469">
            {!isOverlay && <CaseStudyHeader data-id="element-470" />}
            {!isOverlay && <CaseStudyMobileHeader data-id="element-471" />}

            {/* Hero Section */}
            <section className="relative h-screen flex items-center justify-center pt-16 lg:pt-0" data-id="element-472">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png')",
              backgroundPosition: 'center'
            }} data-id="element-473">
                <div className="absolute inset-0 bg-black/60" data-id="element-474"></div>
              </div>

              <div className="relative z-10 w-full max-w-[1080px] px-4 sm:px-6 md:px-8 text-white" data-id="element-475">
                {/* Back to Portfolio button - always visible */}
                {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6 group" aria-label="Back to portfolio" style={{
                marginBottom: '28px'
              }} data-id="element-476">
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-477" />
                    <span className="text-sm font-medium" data-id="element-478">
                      Back to Portfolio
                    </span>
                  </button>}

                {/* Content Update Badge */}
                <div className="mb-4 px-4 py-2 bg-blue-600/90 backdrop-blur-sm rounded-full border border-blue-400/30 shadow-lg inline-flex" data-id="element-479">
                  <p className="text-xs sm:text-sm font-medium text-white flex items-center gap-2" data-id="element-480">
                    <span data-id="element-481">📸</span>
                    <span data-id="element-482">Image and video content updates coming Jan 10th</span>
                  </p>
                </div>

                <p className="text-sm sm:text-base uppercase tracking-wider mb-2 text-[#CCCCCC] font-medium font-switzer mt-4" data-id="element-483">
                  AI COMPARISON 2018
                </p>
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-left leading-tight tracking-tight mb-6" style={{
                fontFamily: "'Gefika', sans-serif",
                fontWeight: 400
              }} data-id="element-484">
                  PROJECT EVE: MONEYGRAM
                </h1>
                <p className="max-w-2xl text-left text-lg sm:text-xl mb-8 font-switzer" data-id="element-485">
                  Enhancing customer service with conversational AI by comparing
                  IBM Watson and Google Dialogflow for MoneyGram Online.
                </p>
              </div>
            </section>

            {/* Overview Section */}
            <section id="overview" className="py-20 px-4 sm:px-6 md:px-8" style={{
            backgroundColor: '#F4F5F8'
          }} data-id="element-486">
              <div className="container mx-auto max-w-[1080px]" data-id="element-487">
                <div className="flex flex-col md:flex-row gap-12" data-id="element-488">
                  <div className="md:w-1/3 relative" data-id="element-489">
                    <div className="sticky top-24" data-id="element-490">
                      <h2 className="text-4xl sm:text-5xl mb-6 tracking-normal text-[#1967d2]" style={{
                      fontFamily: "'Gefika', sans-serif",
                      fontWeight: 400
                    }} data-id="element-491">
                        Overview
                      </h2>
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-492">
                    {/* Mobile Image - Visible only on mobile */}
                    <div className="block lg:hidden mb-8 bg-blue-50 rounded-lg p-8 flex flex-col items-center justify-center border border-blue-100" data-id="element-493">
                      <Image size={48} className="text-blue-300 mb-4" data-id="element-494" />
                      <p className="text-gray-500 text-center max-w-md" data-id="element-495">
                        Image placeholder: MoneyGram conversational AI interface
                        concept
                      </p>
                    </div>

                    <p className="text-base leading-6 text-gray-700 mb-8" data-id="element-496">
                      I was new to both the agency and conversational design
                      when I was asked to take on this project. I was excited by
                      the opportunity to work with AI and innovation, so I{' '}
                      <span className="font-semibold text-slate-900" data-id="element-497">
                        immersed myself in the subject by reading books and
                        reaching out to various NLP/NLU experts
                      </span>{' '}
                      for feedback and insight.
                    </p>
                    {/* Key Results in Overview Section */}
                    <div className="mb-10 rounded-lg p-8 border-l-4 border-[#1967d2] bg-white" data-id="element-498">
                      <h3 className="text-xl font-semibold mb-4 text-[#1967d2]" data-id="element-499">
                        Key Achievements
                      </h3>
                      <ul className="space-y-3" data-id="element-500">
                        <li className="flex items-start" data-id="element-501">
                          <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-[#1967d2]" data-id="element-502" />
                          <span className="text-gray-800" data-id="element-503">
                            Successfully created{' '}
                            <span className="font-semibold text-slate-900" data-id="element-504">
                              working demos of "Track Status" and "Fee Lookup"
                            </span>{' '}
                            functionality
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-505">
                          <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-[#1967d2]" data-id="element-506" />
                          <span className="text-gray-800" data-id="element-507">
                            <span className="font-semibold text-slate-900" data-id="element-508">
                              Secured greenlight and funding
                            </span>{' '}
                            for the next phase of development
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-509">
                          <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-[#1967d2]" data-id="element-510" />
                          <span className="text-gray-800" data-id="element-511">
                            Conducted{' '}
                            <span className="font-semibold text-slate-900" data-id="element-512">
                              comprehensive analysis of conversational AI
                              platforms
                            </span>
                            , evaluating IBM Watson, Google Dialogflow,
                            Microsoft, and Amazon
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-513">
                          <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-[#1967d2]" data-id="element-514" />
                          <span className="text-gray-800" data-id="element-515">
                            Developed{' '}
                            <span className="font-semibold text-slate-900" data-id="element-516">
                              natural dialogue patterns for efficient customer
                              service
                            </span>{' '}
                            that reduced call center burden
                          </span>
                        </li>
                      </ul>
                    </div>
                    <p className="text-base leading-6 text-gray-700 mb-12" data-id="element-517">
                      This case study details Project EVE, a conversational AI
                      initiative for MoneyGram Online aimed at reducing call
                      center volume and enhancing customer service through
                      intelligent automation.
                    </p>
                    {/* Image placeholder - Desktop only */}
                    <div className="hidden lg:flex mb-12 bg-blue-50 rounded-lg p-8 flex-col items-center justify-center border border-blue-100" data-id="element-518">
                      <Image size={48} className="text-blue-300 mb-4" data-id="element-519" />
                      <p className="text-gray-500 text-center max-w-md" data-id="element-520">
                        Image placeholder: MoneyGram conversational AI interface
                        concept
                      </p>
                    </div>
                    {/* Key project details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-521">
                      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#1967d2]" data-id="element-522">
                        <h3 className="text-sm uppercase tracking-wider text-[#1967d2] mb-2 font-semibold" data-id="element-523">
                          Role
                        </h3>
                        <p className="font-medium text-gray-900" data-id="element-524">
                          Conversational Designer
                        </p>
                      </div>
                      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#1967d2]" data-id="element-525">
                        <h3 className="text-sm uppercase tracking-wider text-[#1967d2] mb-2 font-semibold" data-id="element-526">
                          Project Type
                        </h3>
                        <p className="font-medium text-gray-900" data-id="element-527">
                          AI Platform Comparison
                        </p>
                      </div>
                      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#1967d2]" data-id="element-528">
                        <h3 className="text-sm uppercase tracking-wider text-[#1967d2] mb-2 font-semibold" data-id="element-529">
                          Company
                        </h3>
                        <p className="font-medium text-gray-900" data-id="element-530">MoneyGram</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Situation Section */}
            <section id="situation" className="bg-black text-white py-20 px-4 sm:px-6 md:px-8" data-id="element-531">
              <div className="container mx-auto max-w-[1080px]" data-id="element-532">
                <div className="flex flex-col md:flex-row gap-12" data-id="element-533">
                  <div className="md:w-1/3 relative" data-id="element-534">
                    <div className="sticky top-24" data-id="element-535">
                      <div className="flex items-center mb-2" data-id="element-536">
                        <span className="text-gray-400 text-xs sm:text-sm tracking-wider mr-4" data-id="element-537">
                          01
                        </span>
                        <div className="w-6 h-px bg-gray-400" data-id="element-538"></div>
                      </div>
                      <h2 className="text-4xl sm:text-5xl mb-6 tracking-normal" style={{
                      fontFamily: "'Gefika', sans-serif",
                      fontWeight: 400,
                      color: '#1a73e8'
                    }} data-id="element-539">
                        Situation
                      </h2>
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-540">
                    {/* Mobile Image - Visible only on mobile */}
                    <div className="block lg:hidden mb-8 bg-gray-900 rounded-lg p-8 flex flex-col items-center justify-center border border-gray-800" data-id="element-541">
                      <Image size={48} className="text-gray-600 mb-4" data-id="element-542" />
                      <p className="text-gray-400 text-center max-w-md" data-id="element-543">
                        Image placeholder: Call volume data visualization or
                        user journey map
                      </p>
                    </div>

                    <div className="mb-12" data-id="element-544">
                      <h3 className="text-2xl font-semibold mb-6 text-white" data-id="element-545">
                        The Challenge
                      </h3>
                      <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-546">
                        MoneyGram Online was experiencing{' '}
                        <span className="font-semibold text-white" data-id="element-547">
                          high call volumes, with approximately 100,000 calls
                          per month.
                        </span>{' '}
                        As the digital customer base grew, these calls were
                        expected to increase significantly, creating a
                        scalability challenge for their customer service
                        operations.
                      </p>
                      {/* Challenge points in cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-548">
                        <div className="bg-white/5 p-6 rounded-lg backdrop-blur-sm" data-id="element-549">
                          <h3 className="text-white text-lg font-medium mb-3" data-id="element-550">
                            High Call Volume
                          </h3>
                          <p className="text-base text-gray-300" data-id="element-551">
                            100,000+ calls per month were overwhelming the call
                            center, with volume expected to increase as the
                            digital customer base grew.
                          </p>
                        </div>
                        <div className="bg-white/5 p-6 rounded-lg backdrop-blur-sm" data-id="element-552">
                          <h3 className="text-white text-lg font-medium mb-3" data-id="element-553">
                            Repetitive Inquiries
                          </h3>
                          <p className="text-gray-300" data-id="element-554">
                            Most calls were attributed to similar issues that
                            could be solved with a scripted response or an
                            automated solution.
                          </p>
                        </div>
                        <div className="bg-white/5 p-6 rounded-lg backdrop-blur-sm" data-id="element-555">
                          <h3 className="text-white text-lg font-medium mb-3" data-id="element-556">
                            Agent Efficiency
                          </h3>
                          <p className="text-gray-300" data-id="element-557">
                            High Average Handle Time (AHT) for each interaction
                            reduced overall efficiency and increased operational
                            costs.
                          </p>
                        </div>
                        <div className="bg-white/5 p-6 rounded-lg backdrop-blur-sm" data-id="element-558">
                          <h3 className="text-white text-lg font-medium mb-3" data-id="element-559">
                            Platform Selection
                          </h3>
                          <p className="text-gray-300" data-id="element-560">
                            Needed to evaluate and select the right
                            conversational AI platform that would best meet
                            MoneyGram's specific needs.
                          </p>
                        </div>
                      </div>
                      {/* Image placeholder for discovery - Desktop only */}
                      <div className="hidden lg:flex mb-12 bg-gray-900 rounded-lg p-8 flex flex-col items-center justify-center border border-gray-800" data-id="element-561">
                        <Image size={48} className="text-gray-600 mb-4" data-id="element-562" />
                        <p className="text-gray-400 text-center max-w-md" data-id="element-563">
                          Image placeholder: Call volume data visualization or
                          user journey map
                        </p>
                      </div>
                      <h3 className="text-2xl font-semibold mb-6 text-white" data-id="element-564">
                        Discovery
                      </h3>
                      <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-565">
                        During our initial research, we explored the
                        capabilities that would be required for an effective
                        conversational AI solution:
                      </p>
                      <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-300" data-id="element-566">
                        <li data-id="element-567">
                          <span className="font-semibold" data-id="element-568">
                            Functional Requirements:
                          </span>{' '}
                          The bots needed Speech-to-Text (STT), Text-to-Speech
                          (TTS), and Natural Language Understanding (NLU)
                          capabilities.
                        </li>
                        <li data-id="element-569">
                          <span className="font-semibold" data-id="element-570">
                            Platform Evaluation:
                          </span>{' '}
                          We initially gathered data on 90 potential bot
                          platforms before narrowing our focus to those offering
                          the right combination of features.
                        </li>
                        <li data-id="element-571">
                          <span className="font-semibold" data-id="element-572">
                            Documentation Needs:
                          </span>{' '}
                          Comprehensive documentation in both video and written
                          form was essential for troubleshooting and knowledge
                          transfer.
                        </li>
                        <li data-id="element-573">
                          <span className="font-semibold" data-id="element-574">
                            Design Integration:
                          </span>{' '}
                          The platform needed to allow copying from voice
                          interface designs for seamless implementation.
                        </li>
                      </ul>
                      {/* Problem Statement with distinctive styling */}
                      <div className="bg-white p-8 rounded-lg mt-12 shadow-lg relative" data-id="element-575">
                        <div className="absolute -top-4 left-0 px-4 py-1 font-semibold rounded-full text-sm text-white" style={{
                        backgroundColor: '#1967d2'
                      }} data-id="element-576">
                          PROBLEM STATEMENT
                        </div>
                        <p className="text-xl font-semibold leading-relaxed mt-2 text-gray-900" data-id="element-577">
                          "A better solution would help to more accurately
                          categorize calls and reduce Average Handle Time (AHT)
                          for each interaction, improving both customer
                          experience and operational efficiency."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Task Section */}
            <section id="task" className="bg-white py-20 px-4 sm:px-6 md:px-8" data-id="element-578">
              <div className="container mx-auto max-w-[1080px]" data-id="element-579">
                <div className="flex flex-col md:flex-row gap-12" data-id="element-580">
                  <div className="md:w-1/3 relative" data-id="element-581">
                    <div className="sticky top-24" data-id="element-582">
                      <div className="flex items-center mb-2" data-id="element-583">
                        <span className="text-gray-500 text-xs sm:text-sm tracking-wider mr-4" data-id="element-584">
                          02
                        </span>
                        <div className="w-6 h-px bg-gray-400" data-id="element-585"></div>
                      </div>
                      <h2 className="text-4xl sm:text-5xl mb-6 tracking-normal text-[#1a73e8]" style={{
                      fontFamily: "'Gefika', sans-serif",
                      fontWeight: 400
                    }} data-id="element-586">
                        Task
                      </h2>
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-587">
                    {/* Mobile Image - Visible only on mobile */}
                    <div className="block lg:hidden mb-8 bg-gray-100 rounded-lg p-8 flex flex-col items-center justify-center border border-gray-200" data-id="element-588">
                      <Image size={48} className="text-gray-400 mb-4" data-id="element-589" />
                      <p className="text-gray-500 text-center max-w-md" data-id="element-590">
                        Image placeholder: Project timeline or sprint planning
                        diagram
                      </p>
                    </div>

                    <h3 className="text-2xl font-semibold mb-6 text-gray-900" data-id="element-591">
                      Project Goals
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-592">
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-593">
                        <div className="flex items-center mb-3" data-id="element-594">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1967d2] text-white text-xs font-medium mr-3" data-id="element-595">
                            01
                          </span>
                          <h4 className="font-medium text-gray-900" data-id="element-596">
                            Automation
                          </h4>
                        </div>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-597">
                          Automate interactions for the most common customer
                          issues to reduce call center burden
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-598">
                        <div className="flex items-center mb-3" data-id="element-599">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1967d2] text-white text-xs font-medium mr-3" data-id="element-600">
                            02
                          </span>
                          <h4 className="font-medium text-gray-900" data-id="element-601">
                            Experience
                          </h4>
                        </div>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-602">
                          Improve customer experience and agent job quality by
                          handling routine inquiries
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-603">
                        <div className="flex items-center mb-3" data-id="element-604">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1967d2] text-white text-xs font-medium mr-3" data-id="element-605">
                            03
                          </span>
                          <h4 className="font-medium text-gray-900" data-id="element-606">
                            Cost Reduction
                          </h4>
                        </div>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-607">
                          Reduce operational costs through efficient handling of
                          high-volume inquiries
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-608">
                        <div className="flex items-center mb-3" data-id="element-609">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1967d2] text-white text-xs font-medium mr-3" data-id="element-610">
                            04
                          </span>
                          <h4 className="font-medium text-gray-900" data-id="element-611">
                            Platform Evaluation
                          </h4>
                        </div>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-612">
                          Explore capabilities of conversational AI platforms
                          like Google Dialogflow and IBM Watson
                        </p>
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-900" data-id="element-613">
                      Project Scope
                    </h3>
                    <div className="space-y-5 mb-10" data-id="element-614">
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-615">
                        <h4 className="font-medium mb-2 text-gray-900" data-id="element-616">
                          Sprint 1: Platform Selection
                        </h4>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-617">
                          Research conversational AI platforms (voice and chat)
                          and document the criteria for consideration
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-618">
                        <h4 className="font-medium mb-2 text-gray-900" data-id="element-619">
                          Sprint 2: Flow Design
                        </h4>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-620">
                          Understand the platform, document main flows, define
                          intents, and establish context for initial utterances
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-621">
                        <h4 className="font-medium mb-2 text-gray-900" data-id="element-622">
                          Sprint 3: Intent Creation
                        </h4>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-623">
                          Continue designing intents, improve utterances, define
                          fallback options, and begin setting up webhooks
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-624">
                        <h4 className="font-medium mb-2 text-gray-900" data-id="element-625">
                          Sprint 4: Webhook Integration
                        </h4>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-626">
                          Finalize service integrations and enable them in a
                          Google Cloud Function to update or retrieve data from
                          MoneyGram APIs
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg border-l-4 border-[#1967d2] shadow-sm" data-id="element-627">
                        <h4 className="font-medium mb-2 text-gray-900" data-id="element-628">
                          Sprint 5: Experience Optimization
                        </h4>
                        <p className="text-base text-gray-600 leading-relaxed" data-id="element-629">
                          Test voice support, evaluate intent history to improve
                          utterances, and polish the final product for
                          demonstration
                        </p>
                      </div>
                    </div>
                    {/* Image placeholder - Desktop only */}
                    <div className="hidden lg:flex bg-gray-100 rounded-lg p-8 flex-col items-center justify-center border border-gray-200 mb-8" data-id="element-630">
                      <Image size={48} className="text-gray-400 mb-4" data-id="element-631" />
                      <p className="text-gray-500 text-center max-w-md" data-id="element-632">
                        Image placeholder: Project timeline or sprint planning
                        diagram
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Action Section */}
            <section id="action" className="bg-gray-100 py-20 px-4 sm:px-6 md:px-8" data-id="element-633">
              <div className="container mx-auto max-w-[1080px]" data-id="element-634">
                <div className="flex flex-col md:flex-row gap-12" data-id="element-635">
                  <div className="md:w-1/3 relative" data-id="element-636">
                    <div className="sticky top-24" data-id="element-637">
                      <div className="flex items-center mb-2" data-id="element-638">
                        <span className="text-gray-500 text-xs sm:text-sm tracking-wider mr-4" data-id="element-639">
                          03
                        </span>
                        <div className="w-6 h-px bg-gray-400" data-id="element-640"></div>
                      </div>
                      <h2 className="text-4xl sm:text-5xl mb-6 tracking-normal text-[#1a73e8]" style={{
                      fontFamily: "'Gefika', sans-serif",
                      fontWeight: 400
                    }} data-id="element-641">
                        Action
                      </h2>
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-642">
                    <div className="mb-16" data-id="element-643">
                      <h3 className="text-2xl font-semibold mb-6 text-gray-900" data-id="element-644">
                        Bot Discovery & Comparative Analysis
                      </h3>
                      <div className="bg-white p-8 rounded-lg mb-8" data-id="element-645">
                        {/* Mobile Image - Visible only on mobile */}
                        <div className="block lg:hidden mb-6 bg-gray-100 rounded-lg p-6 flex flex-col items-center justify-center border border-gray-200" data-id="element-646">
                          <Image size={36} className="text-gray-400 mb-3" data-id="element-647" />
                          <p className="text-gray-500 text-center" data-id="element-648">
                            Image placeholder: Platform comparison matrix
                          </p>
                        </div>

                        <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-649">
                          We conducted a comprehensive analysis of
                          conversational AI platforms, evaluating offerings from
                          IBM Watson, Google Dialogflow, Microsoft, and Amazon.
                          All top platforms offered excellent documentation and
                          out-of-the-box integrations, but Google's assistant
                          stood out in a recent study for its high accuracy and
                          human-like responses.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-650">
                          <div className="bg-gray-50 p-5 rounded-lg" data-id="element-651">
                            <h5 className="font-medium mb-2 text-gray-900" data-id="element-652">
                              Platforms Evaluated:
                            </h5>
                            <ul className="list-disc pl-5 space-y-1 text-gray-600" data-id="element-653">
                              <li data-id="element-654">IBM Watson</li>
                              <li data-id="element-655">Google Dialogflow</li>
                              <li data-id="element-656">Microsoft Bot Framework</li>
                              <li data-id="element-657">Amazon Lex</li>
                              <li data-id="element-658">Floatbot</li>
                            </ul>
                          </div>
                          <div className="bg-gray-50 p-5 rounded-lg" data-id="element-659">
                            <h5 className="font-medium mb-2 text-gray-900" data-id="element-660">
                              Key Criteria:
                            </h5>
                            <ul className="list-disc pl-5 space-y-1 text-gray-600" data-id="element-661">
                              <li data-id="element-662">Documentation quality</li>
                              <li data-id="element-663">Integration capabilities</li>
                              <li data-id="element-664">Accuracy in understanding queries</li>
                              <li data-id="element-665">Customer support</li>
                              <li data-id="element-666">Developer community</li>
                            </ul>
                          </div>
                        </div>
                        {/* Desktop Image - Visible only on desktop */}
                        <div className="hidden lg:flex bg-gray-100 rounded-lg p-6 flex-col items-center justify-center border border-gray-200" data-id="element-667">
                          <Image size={36} className="text-gray-400 mb-3" data-id="element-668" />
                          <p className="text-gray-500 text-center" data-id="element-669">
                            Image placeholder: Platform comparison matrix
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-16" data-id="element-670">
                      <h3 className="text-2xl font-semibold mb-6 text-gray-900" data-id="element-671">
                        Watson vs. Dialogflow Comparison
                      </h3>
                      <div className="bg-white p-8 rounded-lg" data-id="element-672">
                        <h4 className="text-lg font-medium mb-4 text-gray-900" data-id="element-673">
                          Shared Capabilities
                        </h4>

                        {/* Mobile Images - Visible only on mobile */}
                        <div className="block lg:hidden mb-6" data-id="element-674">
                          <div className="grid grid-cols-1 gap-4" data-id="element-675">
                            <div className="bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center border border-gray-200 h-48" data-id="element-676">
                              <Image size={32} className="text-gray-400 mb-2" data-id="element-677" />
                              <p className="text-gray-500 text-center text-sm" data-id="element-678">
                                Image placeholder: Dialogflow interface
                                screenshot
                              </p>
                            </div>
                            <div className="bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center border border-gray-200 h-48" data-id="element-679">
                              <Image size={32} className="text-gray-400 mb-2" data-id="element-680" />
                              <p className="text-gray-500 text-center text-sm" data-id="element-681">
                                Image placeholder: Watson interface screenshot
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="mb-8" data-id="element-682">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-683">
                            <div className="bg-gray-50 p-5 rounded-lg" data-id="element-684">
                              <h5 className="font-medium mb-2 text-gray-900" data-id="element-685">
                                Intents:
                              </h5>
                              <p className="text-gray-600" data-id="element-686">
                                Both platforms define intents as user actions,
                                though Watson uses dialogue nodes to make them
                                more specific
                              </p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-lg" data-id="element-687">
                              <h5 className="font-medium mb-2 text-gray-900" data-id="element-688">
                                Entities:
                              </h5>
                              <p className="text-gray-600" data-id="element-689">
                                Both use entities as knowledge repositories for
                                personalized and accurate responses
                              </p>
                            </div>
                          </div>
                          <div className="bg-gray-50 p-5 rounded-lg mb-8" data-id="element-690">
                            <h5 className="font-medium mb-2 text-gray-900" data-id="element-691">
                              Slot Filling:
                            </h5>
                            <p className="text-gray-600" data-id="element-692">
                              Both platforms support slot filling to collect
                              multiple pieces of information, using prompts when
                              information is missing
                            </p>
                          </div>
                        </div>
                        {/* Desktop Images - Visible only on desktop */}
                        <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-693">
                          <div className="bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center border border-gray-200 h-48" data-id="element-694">
                            <Image size={32} className="text-gray-400 mb-2" data-id="element-695" />
                            <p className="text-gray-500 text-center text-sm" data-id="element-696">
                              Image placeholder: Dialogflow interface screenshot
                            </p>
                          </div>
                          <div className="bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center border border-gray-200 h-48" data-id="element-697">
                            <Image size={32} className="text-gray-400 mb-2" data-id="element-698" />
                            <p className="text-gray-500 text-center text-sm" data-id="element-699">
                              Image placeholder: Watson interface screenshot
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-700">
                      <h3 className="text-2xl font-semibold mb-6 text-gray-900" data-id="element-701">
                        Conversational Flow Design
                      </h3>
                      <div className="bg-white p-8 rounded-lg" data-id="element-702">
                        {/* Mobile Image - Visible only on mobile */}
                        <div className="block lg:hidden mb-6 bg-gray-100 rounded-lg p-6 flex flex-col items-center justify-center border border-gray-200 h-64" data-id="element-703">
                          <Image size={36} className="text-gray-400 mb-3" data-id="element-704" />
                          <p className="text-gray-500 text-center" data-id="element-705">
                            Image placeholder: Conversational flow diagram for
                            one of the key intents
                          </p>
                        </div>

                        <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-706">
                          Based on our analysis of MoneyGram's customer service
                          data, we designed conversational flows for the most
                          common inquiries. We focused on creating natural
                          dialogue patterns that could efficiently handle
                          customer needs while maintaining a friendly, helpful
                          tone.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-707">
                          <div className="bg-gray-50 p-5 rounded-lg" data-id="element-708">
                            <h5 className="font-medium mb-2 text-gray-900" data-id="element-709">
                              Track Status Flow
                            </h5>
                            <p className="text-gray-600" data-id="element-710">
                              Designed to help customers check the status of
                              their money transfers using reference numbers or
                              personal details
                            </p>
                          </div>
                          <div className="bg-gray-50 p-5 rounded-lg" data-id="element-711">
                            <h5 className="font-medium mb-2 text-gray-900" data-id="element-712">
                              Fee Lookup Flow
                            </h5>
                            <p className="text-gray-600" data-id="element-713">
                              Created to provide customers with accurate fee
                              information based on transfer amount, destination,
                              and payment method
                            </p>
                          </div>
                        </div>
                        {/* Desktop Image - Visible only on desktop */}
                        <div className="hidden lg:flex bg-gray-100 rounded-lg p-6 flex-col items-center justify-center border border-gray-200 h-64" data-id="element-714">
                          <Image size={36} className="text-gray-400 mb-3" data-id="element-715" />
                          <p className="text-gray-500 text-center" data-id="element-716">
                            Image placeholder: Conversational flow diagram for
                            one of the key intents
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Result Section */}
            <section id="result" className="bg-black text-white py-20 px-4 sm:px-6 md:px-8" data-id="element-717">
              <div className="container mx-auto max-w-[1080px]" data-id="element-718">
                <div className="flex flex-col md:flex-row gap-12" data-id="element-719">
                  <div className="md:w-1/3 relative" data-id="element-720">
                    <div className="sticky top-24" data-id="element-721">
                      <div className="flex items-center mb-2" data-id="element-722">
                        <span className="text-gray-400 text-xs sm:text-sm tracking-wider mr-4" data-id="element-723">
                          04
                        </span>
                        <div className="w-6 h-px bg-gray-400" data-id="element-724"></div>
                      </div>
                      <h2 className="text-4xl sm:text-5xl mb-6 tracking-normal" style={{
                      fontFamily: "'Gefika', sans-serif",
                      fontWeight: 400,
                      color: '#1a73e8'
                    }} data-id="element-725">
                        Result
                      </h2>
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-726">
                    <div className="mb-12" data-id="element-727">
                      <h3 className="text-2xl font-semibold mb-6 text-white" data-id="element-728">
                        Proof of Concept Success
                      </h3>

                      {/* Mobile Image - Visible only on mobile */}
                      <div className="block lg:hidden mb-6 bg-gray-900 rounded-lg p-8 flex flex-col items-center justify-center border border-gray-800" data-id="element-729">
                        <Image size={48} className="text-gray-600 mb-4" data-id="element-730" />
                        <p className="text-gray-400 text-center max-w-md" data-id="element-731">
                          Image placeholder: Demo of the conversational AI
                          interface in action
                        </p>
                      </div>

                      <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-732">
                        Project EVE served as Phase 1, where the Innovation
                        Team's goal was to develop a proof of concept and secure
                        funding for the next phase. The{' '}
                        <span className="font-semibold text-white" data-id="element-733">
                          functional demos for "Track Status" and "Fee Lookup"
                          were successful and led to the project being greenlit
                        </span>{' '}
                        for further development.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-734">
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-735">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-736">
                            Functional Demos
                          </h4>
                          <p className="text-gray-300" data-id="element-737">
                            Successfully created working demos of "Track Status"
                            and "Fee Lookup" functionality
                          </p>
                        </div>
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-738">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-739">
                            Project Approval
                          </h4>
                          <p className="text-gray-300" data-id="element-740">
                            Secured greenlight and funding for the next phase of
                            development
                          </p>
                        </div>
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-741">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-742">
                            Technical Validation
                          </h4>
                          <p className="text-gray-300" data-id="element-743">
                            Confirmed technical feasibility and integration
                            capabilities with MoneyGram systems
                          </p>
                        </div>
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-744">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-745">
                            Knowledge Transfer
                          </h4>
                          <p className="text-gray-300" data-id="element-746">
                            Documented platform capabilities and best practices
                            for future development
                          </p>
                        </div>
                      </div>
                      {/* Image placeholder - Desktop only */}
                      <div className="hidden lg:flex mb-12 bg-gray-900 rounded-lg p-8 flex flex-col items-center justify-center border border-gray-800" data-id="element-747">
                        <Image size={48} className="text-gray-600 mb-4" data-id="element-748" />
                        <p className="text-gray-400 text-center max-w-md" data-id="element-749">
                          Image placeholder: Demo of the conversational AI
                          interface in action
                        </p>
                      </div>
                    </div>
                    <div className="mb-12" data-id="element-750">
                      <h3 className="text-2xl font-semibold mb-6 text-white" data-id="element-751">
                        Next Steps
                      </h3>
                      <div className="space-y-6 mb-10" data-id="element-752">
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-753">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-754">
                            Phase 1: Decision Making & Pilot
                          </h4>
                          <ul className="list-disc pl-6 space-y-2 text-gray-300" data-id="element-755">
                            <li data-id="element-756">
                              Finalize platform selection based on comprehensive
                              evaluation
                            </li>
                            <li data-id="element-757">
                              Develop bot persona to embody company voice and
                              give the bot personality
                            </li>
                            <li data-id="element-758">
                              Select 10+ main intents for the pilot with input
                              from MoneyGram internal teams
                            </li>
                          </ul>
                        </div>
                        <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm" data-id="element-759">
                          <h4 className="text-white text-lg font-medium mb-3" data-id="element-760">
                            Handoff Cycle
                          </h4>
                          <ul className="list-disc pl-6 space-y-2 text-gray-300" data-id="element-761">
                            <li data-id="element-762">
                              Transfer proof of concept to development team for
                              further usability testing
                            </li>
                            <li data-id="element-763">
                              Incorporate feedback to improve the bot's
                              performance
                            </li>
                            <li data-id="element-764">
                              Conduct additional testing for accessibility and
                              establish benchmarks for the pilot
                            </li>
                            <li data-id="element-765">
                              Implement user rating system to measure
                              satisfaction and effectiveness
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-766">
                      <h3 className="text-2xl font-semibold mb-6 text-white" data-id="element-767">
                        Key Takeaways
                      </h3>
                      <div className="bg-white/10 p-8 rounded-lg border border-white/10 mb-8" data-id="element-768">
                        <ul className="list-disc pl-6 space-y-4 text-gray-300" data-id="element-769">
                          <li className="text-lg" data-id="element-770">
                            <span className="font-semibold" data-id="element-771">
                              Platform Selection is Critical:
                            </span>{' '}
                            The right conversational AI platform depends on
                            specific use cases, technical requirements, and
                            integration needs.
                          </li>
                          <li className="text-lg" data-id="element-772">
                            <span className="font-semibold" data-id="element-773">
                              Conversation Design Transcends Tools:
                            </span>{' '}
                            Well-designed conversational flows can be
                            implemented on different platforms with appropriate
                            adjustments.
                          </li>
                          <li className="text-lg" data-id="element-774">
                            <span className="font-semibold" data-id="element-775">
                              Proof of Concept Value:
                            </span>{' '}
                            Small, focused demos can effectively demonstrate
                            potential and secure buy-in for larger initiatives.
                          </li>
                          <li className="text-lg" data-id="element-776">
                            <span className="font-semibold" data-id="element-777">
                              Phased Approach Works:
                            </span>{' '}
                            Breaking the project into sprints allowed for
                            iterative learning and adjustment throughout the
                            process.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
            category: 'Case Studies',
            title: 'Cyber Security',
            slug: 'cyber-security'
          }} nextCase={{
            category: 'Featured Work',
            title: 'CWRU',
            slug: 'case-western'
          }} data-id="element-778" />}

            {!isOverlay && <Footer data-id="element-779" />}
          </div>
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-780" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-781" />}
    </>;
}